import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, unref as _unref } from "vue"

const _hoisted_1 = { class: "about" }
const _hoisted_2 = ["label"]
const _hoisted_3 = ["rows"]
const _hoisted_4 = { class: "pt-40" }
const _hoisted_5 = ["rows"]
const _hoisted_6 = ["href"]
const _hoisted_7 = {
  key: 4,
  class: "about-cards"
}
const _hoisted_8 = {
  "icon-name": "info",
  class: "about-card"
}
const _hoisted_9 = { slot: "title" }
const _hoisted_10 = {
  slot: "description",
  class: "download__body"
}
const _hoisted_11 = { class: "download__description" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = {
  key: 0,
  slot: "footer"
}
const _hoisted_14 = ["label", "variant"]
const _hoisted_15 = {
  "icon-name": "tools",
  class: "about-card bundle-card"
}
const _hoisted_16 = { slot: "title" }
const _hoisted_17 = {
  slot: "description",
  class: "support-bundle__body"
}
const _hoisted_18 = { class: "support-bundle__description" }
const _hoisted_19 = { class: "checkbox-label" }
const _hoisted_20 = ["checked", "disabled"]
const _hoisted_21 = { class: "support-bundle__controls" }
const _hoisted_22 = { class: "input-button-row" }
const _hoisted_23 = ["label", "value", "disabled"]
const _hoisted_24 = ["disabled"]
const _hoisted_25 = {
  key: 0,
  slot: "footer"
}
const _hoisted_26 = ["label", "variant"]

import { onMounted, ref, computed } from 'vue';
import { useStore } from 'vuex';
import { MANAGEMENT } from '@shell/config/types';
import { getVendor } from '@shell/config/private-label';
import { downloadFile } from '@shell/utils/download';
import { makeEmptyCell } from '../../../utils/table-formatters';

const downloadLinuxImages = null;


export default /*@__PURE__*/_defineComponent({
  __name: 'about',
  setup(__props) {

const store = useStore();

const version = ref<any>(null);
const settings = ref<any[]>([]);
const fetchError = ref<string>('');
const tailLines = ref<number>(1000);
const includeApps = ref<boolean>(false);
const supportBundleLoading = ref(false);
const supportBundleError = ref('');
const supportBundleSuccess = ref('');

const reportLoading = ref(false);
const reportError = ref('');
const reportSuccess = ref('');

const t = store.getters['i18n/t'];

const aboutTitleString = computed(() => t('about.title'));
const aboutDownloadCLIString = computed(() => t('about.downloadCLI.title'));
const allPackagesString = computed(() => t('epinio.about.allPackages'));

const fetchData = async() => {
  fetchError.value = '';

  try {
    settings.value = await store.dispatch(`management/findAll`, { type: MANAGEMENT.SETTING });
  } catch (err: any) {
    console.warn('Failed to load settings on About page', err);
  }

  try {
    version.value = await store.dispatch('epinio/version');
  } catch (err: any) {
    console.warn('Failed to load version on About page', err);
    fetchError.value = t('epinio.supportBundle.errors.failed');
  }
};

onMounted(fetchData);

const appName = computed(() => {
  const isSingleProduct = !!store.getters['isSingleProduct'];
  return isSingleProduct ? getVendor() : t('epinio.label');
});

function createOSOption(label: string, icon: string, cliLink: string, imageList: any = null) {
  const slash = cliLink?.lastIndexOf('/');
  return {
    label,
    icon,
    imageList,
    cliLink,
    cliFile: slash >= 0 ? cliLink.substr(slash + 1) : cliLink
  };
}

const downloads = computed(() => {
  if (!version.value) {
    return [];
  }

  const gitUrl = `https://github.com/epinio/epinio/releases/download`;
  const versionStr = version.value.displayVersion;
  const app = appName.value.toLowerCase();

  return [
    createOSOption('about.os.mac', 'icon-apple', `${gitUrl}/${versionStr}/${app}-darwin-x86_64`),
    createOSOption('about.os.linux', 'icon-linux', `${gitUrl}/${versionStr}/${app}-linux-x86_64`, downloadLinuxImages),
    createOSOption('about.os.windows', 'icon-windows', `${gitUrl}/${versionStr}/${app}-windows-x86_64.zip`)
  ];
});

const versionString = computed(() => {
  if (!version.value) return '';
  if (version.value.displayVersion === version.value.fullVersion) {
    return version.value.displayVersion;
  }
  return version.value.fullVersion;
});

const sanitizeTail = (value: number | string) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return 1000;
  }

  return Math.max(1, Math.min(Math.round(parsed), 10000));
};

const bundleFileName = () => {
  const now = new Date();
  const pad = (val: number) => val.toString().padStart(2, '0');

  return `epinio-support-bundle-${ now.getFullYear() }-${ pad(now.getMonth() + 1) }-${ pad(now.getDate()) }-${ pad(now.getHours()) }-${ pad(now.getMinutes()) }-${ pad(now.getSeconds()) }.tar.gz`;
};

const reportFileName = () => {
  const now = new Date();
  const pad = (val: number) => val.toString().padStart(2, '0');

  return `epinio-report-${ now.getFullYear() }-${ pad(now.getMonth() + 1) }-${ pad(now.getDate()) }-${ pad(now.getHours()) }-${ pad(now.getMinutes()) }-${ pad(now.getSeconds()) }.txt`;
};

const downloadReport = async() => {
  reportError.value = '';
  reportSuccess.value = '';
  reportLoading.value = true;

  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url:          '/api/v1/report/nodes',
        method:       'get',
        params:       { format: 'text' },
        responseType: 'blob',
        timeout:      60000
      }
    });

    const blob = res?.data;
    const contentType = res?.headers?.['content-type'] || 'text/plain; charset=utf-8';

    await downloadFile(reportFileName(), blob, contentType);

    reportSuccess.value = t('epinio.downloadReport.success');
  } catch (err: any) {
    const status = err?._status || err?.status || err?.response?.status;

    reportError.value = status === 403
      ? t('epinio.downloadReport.errors.unauthorized')
      : t('epinio.downloadReport.errors.failed');
  } finally {
    reportLoading.value = false;
  }
};

const downloadSupportBundle = async() => {
  const safeTail = sanitizeTail(tailLines.value);

  tailLines.value = safeTail;
  supportBundleError.value = '';
  supportBundleSuccess.value = '';
  supportBundleLoading.value = true;

  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url:          '/api/v1/support-bundle',
        method:       'get',
        params:       { tail: safeTail, include_apps: includeApps.value },
        responseType: 'blob',
        timeout:      600000
      }
    });

    const blob = res?.data;
    const contentType = res?.headers?.['content-type'] || 'application/gzip';

    await downloadFile(bundleFileName(), blob, contentType);

    supportBundleSuccess.value = t('epinio.supportBundle.success');
  } catch (err: any) {
    const status = err?._status || err?.status || err?.response?.status;

    supportBundleError.value = status === 403
      ? t('epinio.supportBundle.errors.unauthorized')
      : t('epinio.supportBundle.errors.failed');
  } finally {
    supportBundleLoading.value = false;
  }
};

// Versions table
const versionRows = computed(() => {
  if (!version.value) return [];
  return [{ name: appName.value, version: versionString.value }];
});

const versionColumns = [
  {
    field:     'name',
    label:     t('about.versions.component'),
    formatter: (_v: any, row: any) => {
      const a = document.createElement('a');

      a.href = 'https://github.com/epinio/epinio';
      a.target = '_blank';
      a.rel = 'nofollow noopener noreferrer';
      a.textContent = row.name;

      return a;
    }
  },
  {
    field: 'version',
    label: t('about.versions.version')
  }
];

// Downloads table
const downloadColumns = [
  {
    field:     'label',
    label:     t('about.versions.component'),
    formatter: (_v: any, row: any) => {
      const div = document.createElement('div');

      div.style.cssText = 'display:flex; align-items:center;';

      const icon = document.createElement('i');

      icon.className = `icon ${ row.icon } mr-5`;
      div.appendChild(icon);
      div.appendChild(document.createTextNode(t(row.label)));

      return div;
    }
  },
  {
    field:     'cliFile',
    label:     t('about.downloadCLI.title'),
    formatter: (_v: any, row: any) => {
      if (!row.cliLink) return makeEmptyCell();

      const a = document.createElement('a');

      a.href = row.cliLink;
      a.textContent = row.cliFile;

      return a;
    }
  }
];

const reportStatus = computed(() => {
  if (reportSuccess.value) return { message: reportSuccess.value, variant: 'success' };
  if (reportError.value) return { message: reportError.value, variant: 'error' };
  return null;
});

const bundleStatus = computed(() => {
  if (supportBundleSuccess.value) return { message: supportBundleSuccess.value, variant: 'success' };
  if (supportBundleError.value) return { message: supportBundleError.value, variant: 'error' };
  return null;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (fetchError.value)
      ? (_openBlock(), _createElementBlock("trailhand-tag", {
          key: 0,
          label: fetchError.value,
          variant: "error",
          size: "md",
          class: "mb-20"
        }, null, 8, _hoisted_2))
      : _createCommentVNode("", true),
    _cache[13] || (_cache[13] = _createTextVNode()),
    (version.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("h1", null, _toDisplayString(aboutTitleString.value), 1),
          _cache[2] || (_cache[2] = _createTextVNode()),
          _createElementVNode("trailhand-table", {
            rows: versionRows.value,
            columns: versionColumns,
            searchable: false,
            "key-field": "name",
            class: "version-table"
          }, null, 8, _hoisted_3)
        ], 64))
      : _createCommentVNode("", true),
    _cache[14] || (_cache[14] = _createTextVNode()),
    (version.value && downloads.value.length)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
          _createElementVNode("h3", _hoisted_4, _toDisplayString(aboutDownloadCLIString.value), 1),
          _cache[3] || (_cache[3] = _createTextVNode()),
          _createElementVNode("trailhand-table", {
            rows: downloads.value,
            columns: downloadColumns,
            searchable: false,
            "key-field": "icon",
            class: "downloads-table"
          }, null, 8, _hoisted_5)
        ], 64))
      : _createCommentVNode("", true),
    _cache[15] || (_cache[15] = _createTextVNode()),
    (version.value)
      ? (_openBlock(), _createElementBlock("a", {
          key: 3,
          class: "mt-5",
          target: "_blank",
          href: `https://github.com/epinio/epinio/releases/tag/${version.value.displayVersion}`
        }, _toDisplayString(allPackagesString.value), 9, _hoisted_6))
      : _createCommentVNode("", true),
    _cache[16] || (_cache[16] = _createTextVNode()),
    (version.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createElementVNode("trailhand-card", _hoisted_8, [
            _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(t)('epinio.downloadReport.title')), 1),
            _cache[4] || (_cache[4] = _createTextVNode()),
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("p", _hoisted_11, _toDisplayString(_unref(t)('epinio.downloadReport.description')), 1)
            ]),
            _cache[5] || (_cache[5] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              slot: "action",
              variant: "secondary",
              size: "large",
              disabled: reportLoading.value,
              class: "download__button",
              onClick: downloadReport
            }, _toDisplayString(reportLoading.value ? 'Downloading...' : _unref(t)('epinio.downloadReport.action')), 9, _hoisted_12),
            _cache[6] || (_cache[6] = _createTextVNode()),
            (reportStatus.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_13, [
                  _createElementVNode("trailhand-tag", {
                    label: reportStatus.value.message,
                    variant: reportStatus.value.variant,
                    size: "md"
                  }, null, 8, _hoisted_14)
                ]))
              : _createCommentVNode("", true)
          ]),
          _cache[12] || (_cache[12] = _createTextVNode()),
          _createElementVNode("trailhand-card", _hoisted_15, [
            _createElementVNode("span", _hoisted_16, _toDisplayString(_unref(t)('epinio.supportBundle.title')), 1),
            _cache[10] || (_cache[10] = _createTextVNode()),
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("p", _hoisted_18, _toDisplayString(_unref(t)('epinio.supportBundle.description')), 1),
              _cache[8] || (_cache[8] = _createTextVNode()),
              _createElementVNode("label", _hoisted_19, [
                _createElementVNode("trailhand-checkbox", {
                  checked: includeApps.value,
                  disabled: supportBundleLoading.value,
                  onCheckboxChange: _cache[0] || (_cache[0] = ($event: any) => (includeApps.value = $event.detail.checked))
                }, null, 40, _hoisted_20),
                _createTextVNode(" " + _toDisplayString(_unref(t)('epinio.supportBundle.includeApps')), 1)
              ]),
              _cache[9] || (_cache[9] = _createTextVNode()),
              _createElementVNode("div", _hoisted_21, [
                _createElementVNode("div", _hoisted_22, [
                  _createElementVNode("trailhand-text-input", {
                    label: _unref(t)('epinio.supportBundle.tail.label'),
                    value: String(tailLines.value),
                    size: "large",
                    disabled: supportBundleLoading.value,
                    onTextInputChange: _cache[1] || (_cache[1] = ($event: any) => (tailLines.value = sanitizeTail($event.detail.value)))
                  }, null, 40, _hoisted_23),
                  _cache[7] || (_cache[7] = _createTextVNode()),
                  _createElementVNode("trailhand-button", {
                    variant: "secondary",
                    size: "large",
                    disabled: supportBundleLoading.value,
                    onClick: downloadSupportBundle
                  }, _toDisplayString(supportBundleLoading.value ? _unref(t)('epinio.supportBundle.collecting') : _unref(t)('epinio.supportBundle.action')), 9, _hoisted_24)
                ])
              ])
            ]),
            _cache[11] || (_cache[11] = _createTextVNode()),
            (bundleStatus.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_25, [
                  _createElementVNode("trailhand-tag", {
                    label: bundleStatus.value.message,
                    variant: bundleStatus.value.variant,
                    size: "md"
                  }, null, 8, _hoisted_26)
                ]))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})